import React, { useState, useEffect } from 'react';
import { X, Sparkles } from 'lucide-react';
import * as aiBillingApi from '../../api/aiBilling';

const planMeta = {
  starter: { color: 'border-blue-300 bg-blue-50', badge: 'text-blue-700 bg-blue-100', emoji: '⚡' },
  pro: { color: 'border-purple-300 bg-purple-50', badge: 'text-purple-700 bg-purple-100', emoji: '🚀' },
  unlimited: { color: 'border-amber-300 bg-amber-50', badge: 'text-amber-700 bg-amber-100', emoji: '💎' },
};

export default function UpgradePlanModal({ isOpen, onClose, currentPlan, onUpgraded, message }) {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [upgrading, setUpgrading] = useState(null);
  const [confirmPlan, setConfirmPlan] = useState(null);

  useEffect(() => {
    if (!isOpen) return;
    setLoading(true);
    aiBillingApi.getPlans()
      .then(data => setPlans((data.plans || []).filter(p => p.name !== 'none')))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [isOpen]);

  const handleUpgrade = async (planName) => {
    setUpgrading(planName);
    try {
      await aiBillingApi.upgradePlan(planName);
      onUpgraded?.();
      onClose();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to upgrade plan');
    } finally {
      setUpgrading(null);
      setConfirmPlan(null);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-purple-500" />
            <h2 className="text-lg font-semibold text-gray-900">AI Plans</h2>
          </div>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600 rounded">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Plans grid */}
        <div className="p-6">
          {message && (
            <div className="mb-4 p-3 rounded-lg bg-amber-50 border border-amber-200 text-sm text-amber-800 text-center">
              {message}
            </div>
          )}
          {loading ? (
            <div className="text-center py-8 text-gray-400">Loading plans...</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {plans.map(plan => {
                const meta = planMeta[plan.name] || planMeta.starter;
                const isCurrent = currentPlan === plan.name;

                return (
                  <div key={plan.id} className={`rounded-xl border-2 p-5 transition-all ${
                    isCurrent ? 'border-gray-300 bg-gray-50' : meta.color
                  }`}>
                    <div className="text-center mb-4">
                      <span className="text-2xl">{meta.emoji}</span>
                      <h3 className="text-lg font-bold text-gray-900 mt-2">{plan.display_name}</h3>
                      <div className="mt-1">
                        <span className="text-2xl font-bold text-gray-900">${Number(plan.price_per_month).toFixed(0)}</span>
                        <span className="text-sm text-gray-500">/mo</span>
                      </div>
                    </div>

                    <div className="space-y-2 mb-4 text-sm text-gray-600">
                      <div className="flex items-center gap-2">
                        <span className="text-green-500">✓</span>
                        {plan.monthly_query_limit === null
                          ? 'Unlimited queries'
                          : `${plan.monthly_query_limit} queries/month`}
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-green-500">✓</span>
                        {plan.monthly_deep_analysis_limit === null
                          ? 'Unlimited deep analyses'
                          : `${plan.monthly_deep_analysis_limit} deep analyses/month`}
                      </div>
                      {plan.features?.description && (
                        <p className="text-xs text-gray-400 mt-2">{plan.features.description}</p>
                      )}
                    </div>

                    {isCurrent ? (
                      <div className="text-center">
                        <span className={`inline-block px-3 py-1.5 rounded-lg text-xs font-medium ${meta.badge}`}>
                          Current Plan
                        </span>
                      </div>
                    ) : confirmPlan === plan.name ? (
                      <div className="space-y-2">
                        <p className="text-xs text-gray-500 text-center">Switch to {plan.display_name}?</p>
                        <div className="flex gap-2">
                          <button
                            onClick={() => setConfirmPlan(null)}
                            className="flex-1 px-3 py-1.5 text-xs border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50"
                          >
                            Cancel
                          </button>
                          <button
                            onClick={() => handleUpgrade(plan.name)}
                            disabled={upgrading === plan.name}
                            className="flex-1 px-3 py-1.5 text-xs bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
                          >
                            {upgrading === plan.name ? 'Updating...' : 'Confirm'}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <button
                        onClick={() => setConfirmPlan(plan.name)}
                        className="w-full px-3 py-2 text-sm font-medium bg-white border border-gray-200 rounded-lg text-gray-700 hover:bg-gray-50 hover:border-gray-300 transition-colors"
                      >
                        Select Plan
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          <p className="text-xs text-gray-400 text-center mt-4">
            Plan changes take effect immediately. No payment processing — billing is handled separately.
          </p>
        </div>
      </div>
    </div>
  );
}
